<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTablesStore } from 'dashboard/stores/tables';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  tableId: { type: Number, required: true },
  columns: { type: Array, required: true },
});

const emit = defineEmits(['imported']);

const { t } = useI18n();
const tablesStore = useTablesStore();

const file = ref(null);
const headers = ref([]);
const mapping = ref({});
const result = ref(null);
const errorMessage = ref('');
const isImporting = ref(false);
const isReadingHeaders = ref(false);

// A table with no columns has nothing to map onto, so the mapping step is
// skipped and the file supplies the schema instead.
const createsColumns = computed(() => props.columns.length === 0);
const isReady = computed(() => Boolean(file.value) && !isReadingHeaders.value);

// Headers are read server-side: an .xlsx is a zip archive the browser
// cannot parse, and the server is the source of truth for the file anyway.
const loadHeaders = async selected => {
  isReadingHeaders.value = true;
  try {
    headers.value = await tablesStore.fetchImportHeaders(
      props.tableId,
      selected
    );

    // Pre-map any header that already matches a column name or key, so the
    // common case needs no manual mapping at all.
    mapping.value = Object.fromEntries(
      headers.value.map(header => {
        const match = props.columns.find(
          column =>
            column.name.toLowerCase() === header.toLowerCase() ||
            column.key.toLowerCase() === header.toLowerCase()
        );
        return [header, match?.key || ''];
      })
    );
  } catch (error) {
    errorMessage.value = error?.response?.data?.error || t('TABLES.ERROR');
  } finally {
    isReadingHeaders.value = false;
  }
};

const onFileSelected = event => {
  const selected = event.target.files?.[0];
  result.value = null;
  errorMessage.value = '';
  headers.value = [];
  if (!selected) return;

  file.value = selected;
  if (!createsColumns.value) loadHeaders(selected);
};

const runImport = async () => {
  if (!file.value) return;
  isImporting.value = true;
  errorMessage.value = '';
  try {
    result.value = await tablesStore.importSpreadsheet(
      props.tableId,
      file.value,
      mapping.value
    );
    emit('imported');
  } catch (error) {
    errorMessage.value = error?.response?.data?.error || t('TABLES.ERROR');
  } finally {
    isImporting.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <input
      type="file"
      accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      class="text-sm"
      @change="onFileSelected"
    />

    <!-- Nothing to map onto, so the user is told what will happen instead of
         being shown a row of empty dropdowns. -->
    <div v-if="createsColumns && file" class="flex flex-col gap-2">
      <h3 class="m-0 text-sm font-semibold text-n-slate-12">
        {{ t('TABLES.IMPORT.CREATES_COLUMNS') }}
      </h3>
      <p class="m-0 text-sm text-n-slate-11">
        {{ t('TABLES.IMPORT.CREATES_COLUMNS_HINT') }}
      </p>
    </div>

    <div v-if="!createsColumns && headers.length" class="flex flex-col gap-2">
      <h3 class="m-0 text-sm font-semibold text-n-slate-12">
        {{ t('TABLES.IMPORT.MAP_COLUMNS') }}
      </h3>
      <div v-for="header in headers" :key="header" class="flex items-center gap-2">
        <span class="w-48 text-sm truncate text-n-slate-11">{{ header }}</span>
        <span class="text-n-slate-11">→</span>
        <select
          v-model="mapping[header]"
          class="px-2 py-1 text-sm border rounded border-n-weak bg-transparent text-n-slate-12"
        >
          <option value="">{{ t('TABLES.IMPORT.SKIP') }}</option>
          <option v-for="column in columns" :key="column.key" :value="column.key">
            {{ column.name }}
          </option>
        </select>
      </div>
    </div>

    <Button
      v-if="isReady && (createsColumns || headers.length)"
      :label="t('TABLES.IMPORT.RUN')"
      size="sm"
      class="self-start"
      :is-loading="isImporting"
      @click="runImport"
    />

    <p v-if="errorMessage" class="p-2 m-0 text-sm rounded text-n-ruby-11 bg-n-ruby-3">
      {{ errorMessage }}
    </p>

    <div v-if="result" class="flex flex-col gap-2">
      <p class="m-0 text-sm text-n-slate-12">
        {{ t('TABLES.IMPORT.RESULT', { imported: result.imported, total: result.total }) }}
      </p>
      <!-- Rejected rows are listed with the reason each was refused, rather
           than the whole import failing on the first bad row. -->
      <table v-if="result.rejected?.length" class="w-full text-xs">
        <thead>
          <tr class="text-left text-n-slate-11">
            <th class="py-1">{{ t('TABLES.IMPORT.LINE') }}</th>
            <th class="py-1">{{ t('TABLES.IMPORT.REASON') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="rejected in result.rejected" :key="rejected.line" class="border-t border-n-weak">
            <td class="py-1 pr-2">{{ rejected.line }}</td>
            <td class="py-1 text-n-ruby-11">{{ rejected.errors }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
